import { Button, ButtonWithPopup, Pill, Text } from '@innovationdepartment/proxima-ui';
import { type ReactNode, useState } from 'react';
import DeliverTo, { DeliverToCard, type DeliverToProps } from '../../components/DeliverTo';
import { DeliverToBody, Notice } from '../../components/form.styles';
import { ConfirmActions, ConfirmContent, PrimaryButton } from '../../components/shared.styles';
import { DELIVERY_BLOCKED_REASONS } from '../../lib/definitionDelivery';
import type { AudienceStatus } from '../../types';

type DeliveryPanelProps = DeliverToProps & {
  definitionStatus: AudienceStatus;
};

/**
 * The detail page's "Deliver to" panel: `DeliverTo` for a saved definition.
 *
 * Picking an account that already has the seed adds only the missing lookalikes.
 * A deleted definition shows only the deleted message.
 * Its actions render in the page header through `DeliverHeaderActions`.
 */
const DeliveryPanel = ({ definitionStatus, ...form }: DeliveryPanelProps) => (
  <DeliverToBody>
    {definitionStatus === 'deleted' ? (
      <DeliverToCard>
        <Notice tone="warning">{DELIVERY_BLOCKED_REASONS.deleted}</Notice>
      </DeliverToCard>
    ) : (
      <DeliverTo {...form} />
    )}
  </DeliverToBody>
);

type ConfirmDiscardDeliveryProps = {
  /** True when the Deliver-to panel has a picked account or ratio to lose. */
  confirm: boolean;
  disabled?: boolean;
  placement?: 'bottom-left' | 'bottom-right';
  /** Renders the trigger, wired to `onClick` when no confirm is needed. */
  renderTrigger: (onClick: () => void) => ReactNode;
  onDiscard: () => void;
};

/**
 * Guards an action that discards the Deliver-to selection, as the create page's Cancel does.
 *
 * With a selection to lose, the trigger opens a confirm whose Discard runs `onDiscard`.
 * With nothing picked, the trigger runs `onDiscard` directly.
 */
export const ConfirmDiscardDelivery = ({
  confirm,
  disabled,
  placement = 'bottom-right',
  renderTrigger,
  onDiscard
}: ConfirmDiscardDeliveryProps) => {
  const [open, setOpen] = useState(false);
  if (!confirm) return <>{renderTrigger(onDiscard)}</>;
  return (
    <ButtonWithPopup
      open={open}
      onOpen={() => {
        if (!disabled) setOpen(true);
      }}
      onClose={() => setOpen(false)}
      placement={placement}
      /* ButtonWithPopup opens from its own wrapper, so the trigger needs no handler */
      buttonElement={renderTrigger(() => {})}
    >
      <ConfirmContent>
        <Text variant="body2">Discard this delivery? The picked accounts and ratios are lost.</Text>
        <ConfirmActions>
          <Button
            label="Keep delivery"
            variant="text"
            size="small"
            onClick={() => setOpen(false)}
          />
          <Button
            label="Discard"
            color="error"
            size="small"
            onClick={() => {
              setOpen(false);
              onDiscard();
            }}
          />
        </ConfirmActions>
      </ConfirmContent>
    </ButtonWithPopup>
  );
};

export type DeliverHeaderActionsProps = {
  /** A deleted definition gets Cancel only. */
  deleted: boolean;
  /** True when Cancel would discard a picked account or ratio, so it asks first. */
  confirmDiscard: boolean;
  /** Why Deliver is disabled, or null when it is enabled. */
  blocker: string | null;
  submitting: boolean;
  onDeliver: () => void;
  onCancel: () => void;
};

/**
 * The page header's actions while the Deliver-to panel is open, in the create page's order.
 *
 * The blocker shows as a warning Pill, then Deliver, then Cancel last.
 * Cancel asks before discarding a selection, as the create page's Cancel does on its Deliver step.
 * Deliver and Cancel are disabled while a delivery is submitting, since Cancel cannot stop a request in flight.
 */
export const DeliverHeaderActions = ({
  deleted,
  confirmDiscard,
  blocker,
  submitting,
  onDeliver,
  onCancel
}: DeliverHeaderActionsProps) => (
  <>
    {!deleted && blocker && !submitting ? <Pill variant="warning" label={blocker} /> : null}
    {deleted ? null : (
      <PrimaryButton
        label={submitting ? 'Delivering…' : 'Deliver'}
        size="small"
        disabled={blocker != null || submitting}
        onClick={onDeliver}
      />
    )}
    <ConfirmDiscardDelivery
      confirm={confirmDiscard}
      disabled={submitting}
      onDiscard={onCancel}
      renderTrigger={onClick => (
        <Button
          label="Cancel"
          variant="outlined"
          size="small"
          disabled={submitting}
          onClick={onClick}
        />
      )}
    />
  </>
);

export default DeliveryPanel;
